import { useEffect, useRef, useState } from 'react';
import { authFetch, authHeaders, resolveApiBaseUrl } from '../api/auth';

function imageFromLineItem(item) {
  if (!item || typeof item !== 'object') return null;
  if (typeof item.image_url === 'string' && item.image_url) return item.image_url;
  if (typeof item.image === 'string' && item.image) return item.image;
  if (item.image && typeof item.image === 'object' && item.image.src) return item.image.src;
  if (item.product_image) return item.product_image;
  return null;
}

/**
 * Load product images for visible orders in one batch (local DB), not N+1 Shopify calls.
 */
export default function useProductImages(orders) {
  const [productImagesCache, setProductImagesCache] = useState({});
  const cacheRef = useRef(productImagesCache);
  cacheRef.current = productImagesCache;

  useEffect(() => {
    if (!orders || orders.length === 0) return undefined;

    let cancelled = false;

    const run = async () => {
      const seeded = { ...cacheRef.current };
      const missing = new Set();

      orders.forEach((order) => {
        (order.line_items || []).forEach((item) => {
          const pid = item?.product_id;
          if (!pid) return;
          const key = String(pid);
          if (seeded[key] || seeded[pid]) return;
          const fromItem = imageFromLineItem(item);
          if (fromItem) {
            seeded[key] = fromItem;
            return;
          }
          missing.add(key);
        });
      });

      if (Object.keys(seeded).length > Object.keys(cacheRef.current).length) {
        setProductImagesCache((prev) => ({ ...prev, ...seeded }));
      }

      if (missing.size === 0) return;

      try {
        const base = resolveApiBaseUrl();
        const ids = Array.from(missing).slice(0, 100).join(',');
        const response = await authFetch(`${base}/products/images/?ids=${encodeURIComponent(ids)}`, {
          headers: authHeaders({ 'Content-Type': 'application/json' }),
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        const images = data.images || {};
        if (!cancelled && Object.keys(images).length) {
          setProductImagesCache((prev) => ({ ...prev, ...images }));
        }
      } catch (_) {
        // non-fatal — UI still works without thumbnails
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [orders]);

  return productImagesCache;
}
